import { connectionError } from "@/transport/connection-error"

export { serverFetch } from "@/transport/server"

export class HttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly response?: Response,
  ) {
    super(message)
    this.name = "HttpError"
  }
}

export type TransportFetch = typeof globalThis.fetch

export function defaultTransportFetch(): TransportFetch {
  return globalThis.fetch
}

export function errorMessage(error: unknown, fallback: string): string {
  // A plain-text failure body is already usable, as in `responseError`.
  if (typeof error === "string" && error.trim()) return error.trim()
  if (!error || typeof error !== "object" || Array.isArray(error)) return fallback
  const value = error as { data?: unknown; error?: unknown; message?: unknown }
  if (value.data && typeof value.data === "object" && "message" in value.data) {
    const message = value.data.message
    if (typeof message === "string" && message) return message
  }
  if (typeof value.error === "string" && value.error) return value.error
  if (typeof value.message === "string" && value.message) return value.message
  return fallback
}

export async function responseError(response: Response, fallback?: string) {
  const body = (await response.text().catch(() => "")).trim()
  let message = body
  if (body) {
    try {
      // Structured bodies without a message would otherwise surface as raw JSON.
      message = errorMessage(JSON.parse(body), "")
    } catch {
      // Plain-text failures are already usable.
    }
  }
  return new HttpError(response.status, message || fallback || `Request failed (HTTP ${response.status})`, response)
}

export async function expectResponse(response: Response, options?: { allow?: readonly number[]; error?: string }) {
  if (response.ok || options?.allow?.includes(response.status)) return response
  throw await responseError(response, options?.error)
}

export async function expectJson<T>(
  response: Response,
  decode: (value: unknown) => T,
  options?: { allow?: readonly number[]; error?: string },
) {
  await expectResponse(response, options)
  const value: unknown = await response.json()
  return decode(value)
}

export function transportFetch(input: RequestInfo | URL, init?: RequestInit, customFetch: typeof fetch = fetch) {
  return customFetch(input, init)
}

export function requestFetch(input: RequestInfo | URL, init?: RequestInit, customFetch: typeof fetch = fetch) {
  const headers = new Headers(init?.headers)
  if (!headers.has("Accept")) headers.set("Accept", "application/json")
  return customFetch(input, {
    ...init,
    credentials: init?.credentials ?? "same-origin",
    headers,
  }).catch((error: unknown) => { throw connectionError(error, init?.method, init?.signal) })
}
